'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';

import { PlatformIcon } from 'src/components/platform-icon/platform-icon';

// ----------------------------------------------------------------------

/**
 * **계정 얼굴** — 프로필 이미지 동그라미 + 오른쪽 아래 작은 채널 아이콘 배지.
 * 🔑 얼굴이 없거나 못 불러오면(서명 만료 403 등) **채널 아이콘만** 같은 크기로 선다 — 줄 높이 · 정렬이 그대로다.
 * ⚠️ 채널 구분은 배지가 지킨다 — 이 얼굴 옆에 채널 아이콘을 따로 세우지 않는다.
 * ⛔ 브랜드 로고(가로로 긴 그림)에는 쓰지 않는다 — `HubBrandAvatar`.
 */
export function AccountAvatar({
  src,
  channel,
  size = 24,
  alt = '',
}: {
  /** 얼굴 주소 — 비면 채널 아이콘만 */
  src?: string | null;
  /** 채널 원값(`instagram` · `youtube` …) — 비면 배지 없이 얼굴만(얼굴도 없으면 빈 동그라미) */
  channel?: string | null;
  /** 한 변(px) */
  size?: number;
  alt?: string;
}) {
  const url = String(src ?? '').trim();
  const [broken, setBroken] = useState<string | null>(null);
  const id = String(channel ?? '').trim().toLowerCase();

  if (!url || broken === url) {
    return id
      ? <PlatformIcon platformId={id} size={size} />
      : <Avatar alt={alt} sx={{ width: size, height: size, flexShrink: 0 }} />;
  }

  const badge = Math.max(10, Math.round(size * 0.5));
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <Avatar
        src={url}
        alt={alt}
        slotProps={{ img: { onError: () => setBroken(url) } }}
        sx={{ width: size, height: size, bgcolor: 'grey.100' }}
      />
      {id && (
        <Box
          sx={{
            position: 'absolute',
            right: -2,
            bottom: -2,
            p: '1px',
            lineHeight: 0,
            borderRadius: '50%',
            bgcolor: 'background.paper',
          }}
        >
          <PlatformIcon platformId={id} size={badge} />
        </Box>
      )}
    </Box>
  );
}
